import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  mockEvaluation,
  mockCourse,
  mockStudent,
  mockAssessment,
  mockQuestions,
} from "./MockData";
import { loadMathJaxScript } from "./loadMathJaxScript";
import { Assessment } from "modules/assessment/Assessment";
import { Results } from "modules/questions/Results";

export const typesetMath = (element) => {
  if (window.MathJax) {
    window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub, element]);
  }
};

export const convertMathJax = (text) => {
  if (typeof text !== 'string') return text;
  // Normalize double backslashes to single backslashes so MathJax can recognize the delimiters
  return text.replace(/\\\\/g, '\\');
};
export const shuffleArray = (array) => [...array].sort(() => Math.random() - 0.5);

// Resolve a label from cached master data by category and code
export const getMasterLabel = (category, code) => {
  try {
    if (!code) return code;
    const cached = sessionStorage.getItem("masterDataCache");
    if (!cached) return code;
    const parsed = JSON.parse(cached);
    const list = parsed?.data?.[category] || [];
    const item = list.find(
      (it) => it?.value === code || String(it?.id) === String(code)
    );
    return item?.label || code;
  } catch {
    return code;
  }
};

/**
 * Displays a printable question paper view.
 * @param {object} props - The component props.
 * @param {Array<object>} props.questionsData - The list of questions.
 * @param {string} props.subjectName - The subject name.
 * @param {Function} props.onReturnHome - Callback to return to the home screen.
 */
const QuestionPaper = ({ questionsData, subjectName, onReturnHome }) => {
  const letterChoices = ["A", "B", "C", "D", "E", "F"];
  const questionPaperRef = useRef(null);

  useEffect(() => {
    typesetMath(questionPaperRef.current);
  }, [questionsData]);

  const handlePrint = () => {
    const printContent = questionPaperRef.current.innerHTML;

    const printWindow = window.open("", "", "width=800,height=600");
    if (!printWindow) {
      document.body.innerHTML =
        "Please allow pop-ups for this website to print.";
      return;
    }

    printWindow.document.write("<html><head><title>Question Paper</title>");
    printWindow.document.write(
      '<script src="https://cdn.tailwindcss.com"></script>'
    );
    printWindow.document.write(`<style>
            body {
                font-family: 'Times New Roman', Times, serif;
            }
            @page {
                size: A4;
                margin: 1cm;
            }
            .classic-paper-container {
                width: 100%;
                max-width: 800px;
                margin: 0 auto;
                padding: 1cm;
            }
            .header h1 {
                font-size: 20pt;
            }
            .header p {
                font-size: 10pt;
            }
            .question-text {
                font-size: 11pt;
            }
            .options-table td {
                font-size: 10pt;
            }
            .answer-key-section h2 {
                font-size: 14pt;
            }
            .answer-key-list {
                column-count: 3;
            }
            @media print {
                .print-buttons {
                    display: none;
                }
            }
            .matching-table, .question-table, .open-ended-textarea, .math-calculation {
                border-color: black;
            }
        </style>`);
    printWindow.document.write('</head><body class="bg-white text-black">');
    printWindow.document.write(printContent);
    printWindow.document.write("</body></html>");
    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const PrintIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17.5 14h2a.5.5 0 01.5.5v3a2 2 0 01-2 2h-12a2 2 0 01-2-2v-3a.5.5 0 01.5-.5h2m13-7h-12m12 0H14m2-4H8a2 2 0 00-2 2v6a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2z"
      />
    </svg>
  );

  const HomeIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h4"
      />
    </svg>
  );

  const CustomQuestionRenderer = ({ question, index }) => {
    const questionRef = useRef(null);

    useEffect(() => {
      typesetMath(questionRef.current);
    }, [question]);

    if (!question) return null;

    const { questionType, questionText, details = [] } = question;

    const renderDetail = (detail, detailIndex) => {
      switch (questionType) {
        case "MATCHING":
          const rows = (detail?.questionLeft || "")
            .split(",")
            .map((cell) => cell.trim());
          const cols = (detail?.questionRight || "")
            .split(",")
            .map((cell) => cell.trim());
          return (
            <table
              className="matching-table border border-black border-collapse w-full mt-2 text-sm"
              key={detailIndex}
            >
              <tbody>
                {rows.map((rowText, idx) => {
                  const colText = cols[idx] || "";
                  return (
                    <tr key={idx}>
                      <td className="border border-black p-1">
                        {convertMathJax(rowText)}
                      </td>
                      <td className="border border-black p-1 text-center">⇔</td>
                      <td className="border border-black p-1">
                        {convertMathJax(colText)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          );
        case "QUESTION_TABLE":
          const tableRows = (detail?.questionCsv || "")
            .split("\n")
            .map((line) => line.split(",").map((cell) => cell.trim()));
          return (
            <table
              className="question-table border border-black border-collapse w-full mt-2 text-sm"
              key={detailIndex}
            >
              <tbody>
                {tableRows.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {row.map((cell, colIdx) => (
                      <td
                        key={colIdx}
                        className="border border-black p-1 text-center"
                      >
                        {convertMathJax(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        case "MATHS_CALCULATION":
          return (
            <pre
              key={detailIndex}
              className="math-calculation border border-black p-2 whitespace-pre-wrap mt-2 text-sm"
            >
              {detail?.questionMarkup || "Solve the expression."}
            </pre>
          );
        case "OPEN_ENDED":
          return (
            <textarea
              key={detailIndex}
              placeholder="Your answer..."
              disabled
              className="open-ended-textarea w-full min-h-[80px] border border-black p-1 mt-2 text-sm"
            />
          );
        default:
          return null;
      }
    };

    return (
      <div ref={questionRef} className="question-content">
        <h3 className="question-text text-base font-bold mb-1">
          {convertMathJax(`${index + 1}. ${questionText}`)}
        </h3>
        {details.map((detail, idx) => renderDetail(detail, idx))}
      </div>
    );
  };

  return (
    <div className="bg-white text-black min-h-screen">
      <div
        className="classic-paper-container max-w-4xl mx-auto p-8"
        ref={questionPaperRef}
      >
        <div className="print-buttons flex justify-end gap-4 mb-8">
          <button
            onClick={handlePrint}
            className="p-3 bg-white text-black rounded-full border border-black shadow-md hover:bg-gray-100 transition-colors duration-200"
            title="Print"
          >
            <PrintIcon />
          </button>
          <button
            onClick={onReturnHome}
            className="p-3 bg-white text-black rounded-full border border-black shadow-md hover:bg-gray-100 transition-colors duration-200"
            title="Home"
          >
            Home
          </button>
        </div>

        <div className="header text-center mb-8">
          <h1 className="text-2xl font-bold mb-1">
            {subjectName || "Question Paper"}
          </h1>
          <p className="text-sm">
            <strong>Instructions:</strong> Read each question carefully and
            choose the correct option.
          </p>
        </div>

        <div className="question-section">
          {questionsData.map((question, index) => {
            const sortedChoices = question?.answerChoices
              ?.slice()
              .sort((a, b) => a.id - b.id);
            return (
              <div
                key={question.id}
                className="question-item mb-6 break-inside-avoid"
              >
                <CustomQuestionRenderer question={question} index={index} />
                {sortedChoices?.length > 0 && (
                  <table className="options-table w-full border-collapse mt-2">
                    <tbody>
                      {sortedChoices
                        .reduce((rows, choice, idx) => {
                          if (idx % 2 === 0) {
                            rows.push([choice]);
                          } else {
                            rows[rows.length - 1].push(choice);
                          }
                          return rows;
                        }, [])
                        .map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {row.map((choice, colIndex) => (
                              <td
                                key={choice.id}
                                className="p-1 align-top w-1/2 text-sm"
                              >
                                <strong>
                                  {letterChoices[rowIndex * 2 + colIndex] ||
                                    String.fromCharCode(
                                      65 + rowIndex * 2 + colIndex
                                    )}
                                  .
                                </strong>
                                <span>
                                  {" "}
                                  {convertMathJax(choice.choiceText)}
                                </span>
                              </td>
                            ))}
                            {row.length < 2 && <td />}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>

        <div className="answer-key-section mt-10">
          <h2 className="text-xl font-bold mb-2">Answer Key</h2>
          <ol className="answer-key-list list-decimal pl-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 text-sm">
            {questionsData.map((question) => {
              const correct = question.answerChoices.find((c) => c.isCorrect);
              return (
                <li key={question.id} className="mb-1 break-inside-avoid">
                  <strong>
                    {correct?.choiceText && convertMathJax(correct?.choiceText)}
                    {!correct?.choiceText && "N/A"}
                  </strong>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </div>
  );
};

const AssessmentPage = ({
  onComplete,
  onAssessmentSubmit,
  isSubmitting,
  submitError,
  isQuestionPaperView = false,
  onInit,
}) => {
  const assessment = mockAssessment;
  const [questionsData, setQuestionsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initContext, setInitContext] = useState({
    assignmentId: null,
    evaluation: null,
    course: null,
    student: null,
    timerDuration: null,
  });

  useEffect(() => {
    loadMathJaxScript();
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object") return;
      if (data.type === "assessment:init") {
        const {
          questions = [],
          assignmentId = null,
          evaluation = null,
          course = null,
          student = null,
          timerDuration = null,
        } = data.payload || {};
        setQuestionsData(Array.isArray(questions) ? questions : []);
        setInitContext({
          assignmentId,
          evaluation,
          course,
          student,
          timerDuration,
        });
        try {
          if (onInit)
            onInit({
              assignmentId,
              evaluation,
              course,
              student,
              timerDuration,
            });
        } catch { }
        setLoading(false);
      }
    };
    window.addEventListener("message", onMessage);
    try {
      if (window.opener) {
        window.opener.postMessage({ type: "assessment:ready" }, "*");
      }
    } catch (e) { }
    return () => window.removeEventListener("message", onMessage);
  }, []);

  if (loading) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center bg-transparent p-8">
        <p className="text-2xl font-semibold text-black dark:text-white">
          Loading assessment...
        </p>
      </div>
    );
  }

  return (
    <div className="w-full bg-transparent min-h-screen">
      {isQuestionPaperView ? (
        <QuestionPaper
          questionsData={questionsData}
          subjectName={assessment.subjectName}
          onReturnHome={() => {
            try {
              window.close();
            } catch { }
          }}
        />
      ) : (
        <Assessment
          questionsData={questionsData}
          timerDuration={initContext.timerDuration || assessment.timerDuration}
          onExit={() => {
            try {
              window.close();
            } catch { }
          }}
          onAssessmentSubmit={onAssessmentSubmit}
          isSubmitting={isSubmitting}
          submitError={submitError}
          course={initContext.course}
          evaluation={initContext.evaluation}
        />
      )}
    </div>
  );
};

/**
 * Error screen component shown when submission fails
 */
const SubmissionErrorScreen = ({ errorDetails, onRetry, isRetrying }) => {
  const downloadErrorReport = () => {
    try {
      const report = {
        timestamp: new Date().toISOString(),
        error: errorDetails?.error || {},
        requestPayload: errorDetails?.requestPayload || {},
        responseData: errorDetails?.error?.responseData || null,
        userAgent: navigator.userAgent,
        url: window.location.href,
      };

      const blob = new Blob([JSON.stringify(report, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `assignment-submission-error-${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Failed to download error report:", e);
      alert("Failed to download error report. Please check the console.");
    }
  };

  const copyToClipboard = async () => {
    try {
      const report = {
        timestamp: new Date().toISOString(),
        error: errorDetails?.error || {},
        requestPayload: errorDetails?.requestPayload || {},
        responseData: errorDetails?.error?.responseData || null,
      };
      await navigator.clipboard.writeText(JSON.stringify(report, null, 2));
      alert("Error details copied to clipboard!");
    } catch (e) {
      console.error("Failed to copy to clipboard:", e);
      alert("Failed to copy to clipboard. Please use the download option.");
    }
  };

  const errorMessage =
    errorDetails?.error?.message || "An unknown error occurred during submission";
  const statusCode = errorDetails?.error?.status;
  const statusText = errorDetails?.error?.statusText;

  return (
    <div className="w-full min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="max-w-3xl w-full">
        {/* Main Error Card */}
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border-4 border-red-500 dark:border-red-600 overflow-hidden">
          {/* Error Header */}
          <div className="bg-red-500 dark:bg-red-600 p-8 text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-white dark:bg-gray-900 mb-4">
              <svg
                className="w-16 h-16 text-red-500 dark:text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Submission Failed
            </h1>
            <p className="text-red-100 text-lg">
              We couldn't submit your assignment to the server
            </p>
          </div>

          {/* Error Details */}
          <div className="p-8 space-y-6">
            {/* Error Message */}
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded-r-lg">
              <h3 className="font-bold text-red-800 dark:text-red-300 mb-2">
                Error Details:
              </h3>
              <p className="text-red-700 dark:text-red-400 font-mono text-sm break-words">
                {errorMessage}
              </p>
              {statusCode && (
                <p className="text-red-600 dark:text-red-500 text-sm mt-2">
                  Status: {statusCode} {statusText && `- ${statusText}`}
                </p>
              )}
            </div>

            {/* What Happened */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-lg">
              <h3 className="font-bold text-blue-800 dark:text-blue-300 mb-2">
                What happened?
              </h3>
              <p className="text-blue-700 dark:text-blue-400 text-sm">
                Your answers were recorded locally, but we couldn't send them to the
                server. This could be due to network issues, server problems, or a
                timeout.
              </p>
            </div>

            {/* Next Steps */}
            <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 rounded-r-lg">
              <h3 className="font-bold text-green-800 dark:text-green-300 mb-2">
                Your data is safe!
              </h3>
              <p className="text-green-700 dark:text-green-400 text-sm">
                Your submission data has been preserved. You can retry the submission
                or download the data for manual recovery.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
              <button
                onClick={onRetry}
                disabled={isRetrying || !errorDetails?.requestPayload}
                className={`flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-bold text-lg transition-all duration-200 ${isRetrying || !errorDetails?.requestPayload
                    ? "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  }`}
              >
                {isRetrying ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Retrying...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Retry Submission
                  </>
                )}
              </button>

              <button
                onClick={downloadErrorReport}
                className="flex items-center justify-center gap-3 px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download Error Report
              </button>
            </div>

            {/* Additional Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={copyToClipboard}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium rounded-lg transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                Copy Details
              </button>

              <button
                onClick={() => {
                  try {
                    window.close();
                  } catch (e) {
                    alert("Please close this window manually.");
                  }
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-medium rounded-lg transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Close Window
              </button>
            </div>

            {/* Help Text */}
            <div className="text-center text-sm text-gray-600 dark:text-gray-400 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p>
                If retrying doesn't work, please download the error report and
                contact your instructor with the file.
              </p>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                Error Time:{" "}
                {errorDetails?.error?.timestamp
                  ? new Date(errorDetails.error.timestamp).toLocaleString()
                  : "Unknown"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AssessmentApp = () => {
  const [currentPage, setCurrentPage] = useState("home");
  const [resultsData, setResultsData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submissionErrorDetails, setSubmissionErrorDetails] = useState(null);
  // Renamed `showQuestionPaperButton` to `onlineVsOffline` and made it a boolean to choose which button to render.
  const [online, setOnline] = useState(true);
  const assignmentIdRef = useRef(null);
  const [assessmentContext, setAssessmentContext] = useState({
    student: null,
    course: null,
    evaluation: null,
  });
  const subjectLabelHome = useMemo(
    () => getMasterLabel("subjects", assessmentContext?.course?.subject),
    [assessmentContext?.course?.subject]
  );

  // Listen for init data even on the home screen and announce readiness
  useEffect(() => {
    loadMathJaxScript();
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object") return;
      if (data.type === "assessment:init") {
        const {
          assignmentId = null,
          evaluation = null,
          course = null,
          student = null,
        } = data.payload || {};
        try {
          assignmentIdRef.current = assignmentId;
        } catch { }
        try {
          setAssessmentContext({ student, course, evaluation });
        } catch { }
      } else if (data.type === "assessment:submit-success") {
        // Backend submission successful - show results
        setIsSubmitting(false);
        setSubmitError(null);
        setSubmissionErrorDetails(null);
        const { score, answers, questions } = data.payload || {};
        navigateTo("results", { score, answers, questions });
      } else if (data.type === "assessment:submit-error") {
        // Backend submission failed - show error screen
        setIsSubmitting(false);
        setSubmissionErrorDetails(data.payload);
        setCurrentPage("submit-error");
      }
    };
    window.addEventListener("message", onMessage);
    try {
      if (window.opener) {
        window.opener.postMessage({ type: "assessment:ready" }, "*");
      }
    } catch { }
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const navigateTo = (page, data = null) => {
    setCurrentPage(page);
    if (page === "results" && data) {
      setResultsData(data);
    }
  };

  const handleAssessmentSubmit = async (
    score,
    answers,
    questions,
    questionMetrics,
    timeTakenSeconds
  ) => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmissionErrorDetails(null);
    try {
      const resultPayload = {
        assignmentId: assignmentIdRef.current,
        score,
        submittedDateTime: new Date().toISOString(),
        answers,
        questions,
        timeTaken: timeTakenSeconds,
        questionMetrics,
      };
      if (window.opener) {
        window.opener.postMessage(
          { type: "assessment:complete", payload: resultPayload },
          "*"
        );
      }
      // Don't navigate to results immediately - wait for backend confirmation
      // The message listener will handle navigation on success or show error on failure
    } catch (error) {
      setSubmitError(error.message);
      setIsSubmitting(false);
      setSubmissionErrorDetails({
        error: { message: error.message, timestamp: new Date().toISOString() },
        requestPayload: null,
        originalData: { score, answers, questions },
      });
      setCurrentPage("submit-error");
    }
  };

  const studentContext = assessmentContext.student || mockStudent;
  const courseContext = assessmentContext.course || mockCourse;
  const evaluationContext = assessmentContext.evaluation || mockEvaluation;

  switch (currentPage) {
    case "home":
      return (
        <div className="w-full min-h-screen flex items-center justify-center bg-transparent p-8">
          <div className="flex flex-col lg:flex-row w-full max-w-5xl gap-8">
            {/* Left Column */}
            <div className="flex-1 space-y-6">
              {/* Course Details Card */}
              <div className="bg-white dark:bg-gray-900 p-8 rounded-3xl text-left border border-gray-200 dark:border-gray-800 shadow-lg">
                <h2 className="text-3xl font-bold text-black dark:text-white mb-4 border-b pb-2 border-gray-200 dark:border-gray-800">
                  Course Details
                </h2>
                <p className="text-lg text-gray-700 dark:text-gray-300">
                  <strong className="font-semibold text-black dark:text-white">
                    Course:
                  </strong>{" "}
                  {courseContext?.courseName}
                </p>
                <p className="text-lg text-gray-700 dark:text-gray-300 mt-2">
                  <strong className="font-semibold text-black dark:text-white">
                    Assessment:
                  </strong>{" "}
                  {evaluationContext?.name}
                </p>
                <p className="text-lg text-gray-700 dark:text-gray-300 mt-2">
                  <strong className="font-semibold text-black dark:text-white">
                    Subject:
                  </strong>{" "}
                  {subjectLabelHome || "-"}
                </p>
              </div>
              {/* Instructions Card */}
              <div className="bg-white dark:bg-gray-900 p-8 rounded-3xl border border-red-400 dark:border-red-600 shadow-lg">
                <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">
                  Important Instructions
                </h2>
                <ul className="text-lg text-red-700 dark:text-red-300 list-disc pl-5 space-y-2">
                  <li>Do not use your browser's back or forward buttons.</li>
                  <li>
                    Click the "Submit" button at the end of the assessment to
                    successfully register your responses.
                  </li>
                  <li>Ensure a stable internet connection before starting.</li>
                </ul>
              </div>
            </div>

            {/* Right Column */}
            <div className="flex-1 flex items-center justify-center">
              <div className="bg-white dark:bg-gray-900 p-12 rounded-3xl text-center w-full border border-gray-200 dark:border-gray-800 shadow-lg">
                <h1 className="text-4xl font-extrabold text-black dark:text-white mb-4">
                  Assessment App
                </h1>
                <p className="text-lg text-gray-700 dark:text-gray-300 mb-2">
                  Welcome,{" "}
                  <strong className="text-blue-600 dark:text-blue-400">
                    {studentContext.name}
                  </strong>
                  !
                </p>
                <p className="text-lg text-gray-700 dark:text-gray-300 mb-8">
                  You are taking the {evaluationContext?.name} for the{" "}
                  {courseContext?.courseName} course.
                </p>
                <div className="flex flex-col space-y-4">
                  {/* Conditionally render one button based on the `onlineVsOffline` state */}
                  {online ? (
                    <button
                      className="w-full px-8 py-4 bg-blue-500 text-white font-bold rounded-full transition-colors duration-200 hover:bg-blue-600"
                      onClick={() => navigateTo("assessment")}
                    >
                      Start Assessment
                    </button>
                  ) : (
                    <button
                      className="w-full px-8 py-4 bg-purple-500 text-white font-bold rounded-full transition-colors duration-200 hover:bg-purple-600"
                      onClick={() => navigateTo("question-paper")}
                    >
                      View Question Paper
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    case "assessment":
      return (
        <AssessmentPage
          onComplete={navigateTo}
          onAssessmentSubmit={handleAssessmentSubmit}
          isSubmitting={isSubmitting}
          submitError={submitError}
          onInit={({ assignmentId, student, course, evaluation }) => {
            try {
              assignmentIdRef.current = assignmentId;
            } catch { }
            try {
              setAssessmentContext({ student, course, evaluation });
            } catch { }
          }}
        />
      );
    case "question-paper":
      return (
        <AssessmentPage onComplete={navigateTo} isQuestionPaperView={true} />
      );
    case "results":
      return (
        <Results
          score={resultsData?.score}
          answers={resultsData?.answers}
          questions={resultsData?.questions}
          onRetake={() => navigateTo("assessment")}
          onReturnHome={() => {
            try {
              window.close();
            } catch { }
          }}
        />
      );
    case "submit-error":
      return (
        <SubmissionErrorScreen
          errorDetails={submissionErrorDetails}
          onRetry={() => {
            if (submissionErrorDetails?.requestPayload && window.opener) {
              setIsSubmitting(true);
              window.opener.postMessage(
                {
                  type: "assessment:retry-submit",
                  payload: {
                    requestPayload: submissionErrorDetails.requestPayload,
                    originalData: submissionErrorDetails.originalData,
                  },
                },
                "*"
              );
            }
          }}
          isRetrying={isSubmitting}
        />
      );
    default:
      return (
        <div className="w-full min-h-screen flex items-center justify-center bg-transparent">
          <p className="text-2xl font-semibold text-red-500 dark:text-red-400">
            Page Not Found
          </p>
        </div>
      );
  }
};

export default AssessmentApp;
